/**
 * The settlement print and the seconds after it, as arithmetic
 * (`components.md` §2.8).
 *
 * `<ResolutionPanel>` is a headline moment, not a loading state: the settlement
 * price is *being decided in front of the user*, the outcome is genuinely
 * undetermined until it lands, and it can flip. This file is the number and the
 * phase; the component is the pixels.
 *
 * ## One print, not a window (§F73, §F74)
 *
 * A market settles on the Chainlink print stamped at its expiry `T`, the same
 * print that fixes the next market's strike. There is no averaging window any
 * more: the market's `settlement_window_*_ms` are the zero-width `[T, T)`.
 *
 * ## Where the live number comes from, and why it is not the server's
 *
 * `market.{id}` publishes `resolution` only at `RESOLVED` — the object's own
 * contract is "this object only ever contains a final number"
 * (`openapi.Resolution`). Until then the honest live number is the one the
 * client can read for itself off the **index**: the tick stamped at `T` once the
 * socket delivers it — the print the resolver settles on — and before that the
 * newest tick at or before `T`. The panel shows it labelled provisional and
 * **settles onto the server's value the moment `RESOLVED` lands** — §2.8's
 * "must settle *onto* the final value rather than treating its own last frame as
 * final", made mechanical.
 *
 * No React, no DOM.
 */

import { formatUtcTime } from '@/lib/format'
import type { Observation } from './price'
import { impliedOutcome, observationFrom } from './price'
import type { MarketState } from './lifecycle'
import { isDegraded, isVoidPath } from './lifecycle'

/**
 * The client's estimate of the settlement print: the newest index tick stamped
 * at or before the settlement instant `T`.
 *
 * Once the tick stamped `T` arrives — usually a second or two after it — that is
 * exactly the print the resolver settles on, and nothing after it may replace
 * it: a tick past `T` is the next market's price. Before it arrives, the newest
 * earlier tick is the price the market would settle on if nothing else printed.
 * One observation with its own `expo`, never a combination of several, so a
 * stream that changes exponent cannot mix two scales (invariant 11).
 */
export class SettlementPrint {
  readonly #atMs: number
  #best: Observation | null = null
  #bestAtMs = -Infinity

  constructor(atMs: number) {
    this.#atMs = atMs
  }

  /**
   * Offer one index tick, stamped `atMs` by the oracle. Returns whether it
   * became the estimate: it must be at or before `T`, and strictly newer than
   * the one held — `index.{symbol}` is at-least-once, so the same tick is
   * legitimately delivered twice.
   */
  push(source: unknown, atMs: number): boolean {
    if (atMs > this.#atMs || atMs <= this.#bestAtMs) return false
    const observation = observationFrom(source)
    if (!observation) return false
    this.#best = observation
    this.#bestAtMs = atMs
    return true
  }

  /** The estimate, or `null` before any tick at or before `T` has arrived. */
  value(): Observation | null {
    return this.#best
  }

  /** Whether the estimate is the print stamped at `T` itself. */
  get exact(): boolean {
    return this.#bestAtMs === this.#atMs
  }
}

// ---------------------------------------------------------------------------
// The phase
// ---------------------------------------------------------------------------

/**
 * The sentence that says how a price was made — `docs/07` §4's footer, owned
 * here so `<ResolutionPanel>` and `<SettlementReceipt>` cannot disagree:
 * `print at {hh:mm:ss}`, UTC, for the print stamped at `atMs`. `''` with no
 * instant to describe.
 */
export function observationSentence(atMs: number | null): string {
  return typeof atMs === 'number' ? `print at ${formatUtcTime(atMs)}` : ''
}

/**
 * The same sentence read straight off an observation that carries its own
 * bounds — `StrikeObservation` and `SettlementPrice` both publish
 * `window_end_ms`, the instant of the print — for `<StrikeMeter>`'s corner.
 */
export function observationTag(source: unknown): string {
  if (typeof source !== 'object' || source === null) return ''
  const end = (source as Record<string, unknown>).window_end_ms
  return observationSentence(typeof end === 'number' ? end : null)
}

export type ResolutionPhase =
  /** Before the settlement instant. The market is trading; the panel's number is
   *  the live index, and the outcome can still flip. */
  | 'PENDING'
  /** `[settlement_window_start_ms, expiry)`. Empty since §F74 — the settlement
   *  is one print at expiry, so the window is `[expiry, expiry)` — and kept only
   *  so a payload that published a window apart from expiry still renders. */
  | 'SAMPLING'
  /** `[expiry, resolve_eta)`. **Not a stall** — the print is stamped at expiry
   *  and the resolver is publishing. §2.8: it must not be styled as an anomaly. */
  | 'CONFIRMING'
  /** Past `resolve_eta` with no `RESOLVED`. The bar goes indeterminate. */
  | 'LATE'
  /** `RESOLVED` / `SETTLED`. The panel stops moving. */
  | 'FINAL'
  /** `VOIDED` / `REFUNDED`. */
  | 'VOID'

export interface ResolutionInput {
  state: MarketState
  /** Also `settlement_window_end_ms` — the settlement print is stamped at
   *  expiry. Read both off the payload rather than assuming they coincide. */
  expiryAtMs: number
  /** `settlement_window_start_ms` off the payload — equal to expiry for a print. */
  settlementStartMs: number
  resolveEtaMs: number | null
  nowMs: number
}

export interface ResolutionView {
  phase: ResolutionPhase
  /** `0…1` over `[expiry, resolve_eta]`, or `null` for indeterminate — which is
   *  the only honest render once `resolve_eta_ms` has passed with nothing to
   *  show for it. Inventing progress the server has not reported is a lie. */
  progress: number | null
  degraded: boolean
  /** Whether the number on screen is the client's own estimate. Everything the
   *  UI does differently in this state — `LEANING`, never `WON` — hangs off it. */
  provisional: boolean
}

export function resolutionView(input: ResolutionInput): ResolutionView {
  const { state, expiryAtMs, settlementStartMs, resolveEtaMs, nowMs } = input
  const degraded = isDegraded(state)

  if (isVoidPath(state)) return { phase: 'VOID', progress: 1, degraded, provisional: false }
  if (state === 'RESOLVED' || state === 'SETTLED') {
    return { phase: 'FINAL', progress: 1, degraded, provisional: false }
  }

  // The arc spans the whole of what the user watches — from the settlement
  // instant to the instant the number is final.
  const progress =
    resolveEtaMs === null || resolveEtaMs <= settlementStartMs ?
      null
    : Math.max(0, Math.min(1, (nowMs - settlementStartMs) / (resolveEtaMs - settlementStartMs)))

  if (resolveEtaMs !== null && nowMs >= resolveEtaMs) {
    return { phase: 'LATE', progress: null, degraded, provisional: true }
  }
  if (nowMs < settlementStartMs) return { phase: 'PENDING', progress, degraded, provisional: true }
  if (nowMs < expiryAtMs) return { phase: 'SAMPLING', progress, degraded, provisional: true }
  return { phase: 'CONFIRMING', progress, degraded, provisional: true }
}

/**
 * The leaning outcome, on the exact integer comparison.
 *
 * `settle >= strike` resolves UP and the tie belongs to UP (D5). Nothing here
 * weakens that to a float comparison of two `toNumber`s, because the tie rule is
 * the one place a last-digit error changes who gets paid — even though what this
 * particular call decides is a word on a provisional pill.
 */
export function leaningOutcome(
  settlement: Observation | null,
  strike: Observation | null,
): 'UP' | 'DOWN' | null {
  return impliedOutcome(settlement, strike)
}
